import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { Building, Users, MapPin, Zap, ArrowRight, CreditCard } from 'lucide-react';

function KpiCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-[#1E293B] rounded-2xl p-6 border border-[#334155]">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${color}20` }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div className="text-3xl font-bold text-white tabular-nums tracking-tight">{value?.toLocaleString('it-IT') ?? '—'}</div>
      <div className="text-[#64748B] text-sm mt-1">{label}</div>
    </div>
  );
}

export default function SuperadminDashboard() {
  const [stats, setStats] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminApi.get('/superadmin/stats'),
      adminApi.get('/superadmin/brands'),
    ]).then(([sRes, bRes]) => {
      setStats(sRes.data);
      setBrands(bRes.data.brands || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="superadmin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Dashboard Lazo</h1>
        <p className="text-[#64748B] text-base mt-1">Panoramica piattaforma</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-10">
        <KpiCard label="Brand" value={stats?.total_brands} icon={Building} color="#D44E20" />
        <KpiCard label="Punti Vendita" value={stats?.total_venues} icon={MapPin} color="#F59E0B" />
        <KpiCard label="Clienti totali" value={stats?.total_customers} icon={Users} color="#3B82F6" />
        <KpiCard label="Attivi (30gg)" value={stats?.active_customers_30d} icon={Zap} color="#10B981" />
        <KpiCard label="Transazioni (30gg)" value={stats?.transactions_30d} icon={CreditCard} color="#8B5CF6" />
        <KpiCard label="Staff attivi" value={stats?.total_staff} icon={Users} color="#EC4899" />
      </div>

      {/* Brands list */}
      <div className="bg-[#1E293B] rounded-2xl border border-[#334155] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#334155] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Brand attivi</h2>
          <a href="/superadmin/brands" data-testid="sa-view-all-brands"
            className="text-sm text-[#D44E20] hover:text-[#F59E0B] flex items-center gap-1 transition-colors">
            Vedi tutti <ArrowRight size={14} />
          </a>
        </div>
        <div className="divide-y divide-[#334155]">
          {brands.map(b => (
            <a href={`/superadmin/brands`} key={b.id}
              className="flex items-center gap-4 px-6 py-4 hover:bg-[#334155]/50 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#334155] flex items-center justify-center flex-shrink-0 overflow-hidden">
                {b.logo_url ? (
                  <img src={b.logo_url} alt="" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-[#D44E20] font-bold text-lg">{b.name?.charAt(0)}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm">{b.name}</p>
                <p className="text-[#64748B] text-xs">{b.venues_count} venue &middot; {b.customers_count} clienti &middot; {b.staff_count} staff</p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                b.plan === 'pro' ? 'bg-[#D44E20]/20 text-[#D44E20]' : 'bg-[#334155] text-[#94A3B8]'
              }`}>
                {b.plan}
              </span>
            </a>
          ))}
          {brands.length === 0 && (
            <div className="px-6 py-8 text-center text-[#64748B]">Nessun brand registrato</div>
          )}
        </div>
      </div>
    </div>
  );
}
